<!--
// app/components/pages/region-stats/GridFeaturedStatsPlayersOverview.vue
-->
<script setup lang="ts">
import type { SeasonStatsPartner } from "~/src/season-cluster/domain/models/01.Season";
import BaseBackdropLoading from "~/app/components/base/BaseBackdropLoading.vue";
import { useRegions } from "~/app/composables/regions";
import CardPlayerRankingCards from "~/app/components/pages/region-stats/cards-featured-stats/CardPlayerRankingCards.vue";
import CardBestGoalkeeper from "~/app/components/pages/region-stats/cards-featured-stats/CardBestGoalkeeper.vue";
import CardPlayerRankingGeneralNoPartner from "~/app/components/pages/region-stats/cards-featured-stats/CardPlayerRankingGeneralNoPartner.vue";
import CardPlayerMvpList from "~/app/components/pages/region-stats/CardPlayerMvpList.vue";
import { $SEASONS_WITHOUT_DATA } from "~/src/season-cluster/infrastructure/ui-services/seasonUiService";

type Props = {
  loading: boolean;
  partner?: SeasonStatsPartner;
  seasonId?: number | string;
  bestGkId?: number | string;
};
const props = defineProps<Props>();

const { locale } = useI18n();
const { isWorldCup, isSpain, isNations } = useRegions();
</script>

<template>
  <div class="text-gray-400 uppercase text-sm mb-2">{{ $t("general.stats.title") }}</div>
  <hr />
  <div class="main-container-cards">
    <base-backdrop-loading :active="loading"></base-backdrop-loading>

    <card-player-mvp-list
      :season-id="seasonId"
      :has-partner="!!partner"
      :partner-logo="partner?.logo"
      :partner-name="partner?.name"
      :table-stat-title="$t('general.stats.mvps')"
      page-stats-link="Mvp"
      is-stats
    ></card-player-mvp-list>

    <card-player-ranking-general-no-partner
      v-if="!!seasonId"
      :season-id="seasonId"
      :page-stats-link="!$SEASONS_WITHOUT_DATA.includes(Number(seasonId)) ? 'GoalsGrouped' : 'Goals'"
      parameter-string="GOL"
      :pre-title-stat="locale === 'it' ? $t('cards.stats.pretitle.best') : $t('cards.stats.pretitle.top')"
      :title-stat="$t('cards.stats.title.scorer')"
      :table-stat-title="$t('general.stats.goals')"
      is-stats
    />

    <card-player-ranking-general-no-partner
      v-if="!!seasonId"
      :season-id="seasonId"
      page-stats-link="Passing"
      parameter-string="ASS-V"
      :pre-title-stat="$t('cards.stats.pretitle.top')"
      :title-stat="$t('cards.stats.title.assistant')"
      :table-stat-title="$t('general.stats.assists')"
      is-stats
    />

    <card-best-goalkeeper
      v-if="!!seasonId && !$SEASONS_WITHOUT_DATA.includes(Number(seasonId)) && !isNations"
      :season-id="seasonId"
      page-stats-link="Goalkeeper"
      is-stats
      :disclaimer="isWorldCup && seasonId === 13 ? $t('cards.stats.disclaimer.bestGkKWC') : undefined"
      :is-best-gk="!!bestGkId"
      :best-gk-id="bestGkId"
    />

    <card-player-ranking-cards v-if="!!seasonId" :season-id="seasonId" />

    <card-player-ranking-general-no-partner
      v-if="!!seasonId && !$SEASONS_WITHOUT_DATA.includes(Number(seasonId))"
      :season-id="seasonId"
      page-stats-link="Offensives"
      parameter-string="DRB"
      :title-stat="$t('cards.stats.title.dribbling')"
      :table-stat-title="$t('Drib')"
      is-stats
    />
  </div>
</template>

<style scoped>
.main-container-cards {
  @apply grid grid-cols-1 justify-items-center sm:grid-cols-2 md:grid-cols-3 md:grid-rows-[repeat(2,minmax(0,568px))] gap-x-4 gap-y-[35px] mt-4 relative;
}
</style>
